<template>
  <div>
    <v-data-table
      :headers="tableHeaders"
      :items="displayRows"
      :items-per-page="-1"
      :hide-default-footer="true"
      :item-class="rowClass"
      dense
      class="elevation-1 war-import-preview"
    >
      <template #[`item.expand`]="{ item }">
        <v-icon
          v-if="item.rowType === 'group'"
          small
          tabindex="0"
          role="button"
          :aria-label="isExpanded(item) ? 'Collapse fund detail' : 'Expand fund detail'"
          class="war-import-preview-toggle"
          @click="toggleGroup(item)"
          @keyup.enter="toggleGroup(item)"
          @keyup.space="toggleGroup(item)"
        >
          {{ isExpanded(item) ? icons.mdiChevronUp : icons.mdiChevronDown }}
        </v-icon>
      </template>
      <template #[`item.amount`]="{ item }">
        {{ formatAmount(item.amount) }}
      </template>
      <template #[`item.status`]="{ item }">
        <v-chip
          small
          :color="statusColor(item.status)"
          text-color="white"
        >{{ statusLabel(item.status) }}</v-chip>
      </template>
    </v-data-table>
    <div class="d-flex justify-end mt-3">
      <div class="text-right">
        <div class="text-subtitle-1 font-weight-bold">
          Grand total ({{ preview.length }} warrant{{ preview.length === 1 ? '' : 's' }}):
          {{ formatAmount(grandTotal) }}
        </div>
        <div
          v-if="willCreateTotal !== grandTotal"
          class="text-caption"
        >
          To be created ({{ willCreateRows.length }}): {{ formatAmount(willCreateTotal) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mdiChevronDown, mdiChevronUp } from '@mdi/js';
import {
  buildFundGroups,
  flattenPreviewRows,
  previewStatusColor,
  previewStatusLabel,
  sumPreviewAmounts,
  willCreatePreviewRows,
} from './warrantPreview.utils';

export default {
  name: 'WarrantImportPreview',
  props: {
    preview: {
      type: Array,
      default: () => [],
    },
    allowSeqErrorImport: {
      type: Boolean,
      default: () => false,
    },
    hasBadDates: {
      type: Boolean,
      default: () => false,
    },
  },
  data: () => ({
    icons: {
      mdiChevronDown,
      mdiChevronUp,
    },
    expandedKeys: [],
    tableHeaders: [
      {
        text: '', value: 'expand', sortable: false, width: '40px',
      },
      { text: 'FY', value: 'fiscalYear', sortable: false },
      { text: 'Fund', value: 'fund', sortable: false },
      { text: 'Department', value: 'department', sortable: false },
      { text: 'Account', value: 'account', sortable: false },
      { text: 'Warrant Number', value: 'warrantDisplay', sortable: false },
      {
        text: 'Amount', value: 'amount', sortable: false, align: 'end',
      },
      { text: 'Status', value: 'status', sortable: false },
      { text: 'Reason', value: 'reason', sortable: false },
    ],
  }),
  computed: {
    fundGroups() {
      return buildFundGroups(this.preview);
    },
    displayRows() {
      return flattenPreviewRows(this.fundGroups, this.expandedKeys);
    },
    grandTotal() {
      return sumPreviewAmounts(this.preview);
    },
    willCreateRows() {
      return willCreatePreviewRows(this.preview, {
        allowSeqErrorImport: this.allowSeqErrorImport,
        hasBadDates: this.hasBadDates,
      });
    },
    willCreateTotal() {
      return sumPreviewAmounts(this.willCreateRows);
    },
  },
  methods: {
    statusColor(status) {
      return previewStatusColor(status);
    },
    statusLabel(status) {
      return previewStatusLabel(status);
    },
    formatAmount(value) {
      const amount = Number(value) || 0;
      return amount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    isExpanded(item) {
      return this.expandedKeys.includes(item.key);
    },
    toggleGroup(item) {
      if (this.isExpanded(item)) {
        this.expandedKeys = this.expandedKeys.filter((k) => k !== item.key);
      } else {
        this.expandedKeys = this.expandedKeys.concat([item.key]);
      }
    },
    rowClass(item) {
      return item.rowType === 'group'
        ? 'war-import-preview-group-row'
        : 'war-import-preview-detail-row';
    },
  },
};
</script>

<style scoped>
/* row classes are applied via :item-class directly onto Vuetify's own <tr>
   elements (not this component's slot content), so they need ::v-deep to match. */
::v-deep .war-import-preview-group-row > td {
  font-weight: 700;
}
::v-deep .war-import-preview-detail-row > td {
  background-color: rgba(0, 0, 0, 0.03);
}
.war-import-preview-toggle {
  cursor: pointer;
}
</style>
